import fontFace from 'system/fontFace';

const breakpoints = {
  mobile: 375,
  tablet: 768,
  desktopTiny: 960,
  desktopSmall: 1024,
  desktop: 1280,
  desktopLarge: 1920
};

const responsiveGrid = {
  mobile: 298,
  tablet: 612,
  desktopTiny: 924,
  desktopSmall: 1165,
  desktop: 1252,
  desktopLarge: 1252
};

/**
 * The `themes` object contains style variables for each given theme.
 */
const themes = {
  legacy: {
    responsiveGrid,
    breakpoints,
    theme: 'legacy',
    layoutManager: {
      fontFamily: '"Roboto", Helvetica, Arial, sans-serif',
      marginTop: -50,
      '& > .glu-base-nav': {
        position: 'fixed',
        zIndex: 1002,
        width: '100%'
      }
    },

    mainContent: {
      paddingTop: 107, // page-headline height + height of rainbow bar
    },

    pageHeadline: {
      position: 'fixed',
      zIndex: 1000,
      top: 50,
      width: '100%',
      maxWidth: 1820,
      background: '#4c4c4c',
      height: 50,
      margin: 0
    },
    customPageHeadline: {
      width: '100%',
      position: 'fixed',
      zIndex: 1000,
      top: 50,
      '& > .page-headline': {
        margin: 0
      }
    },
    gluContainer: {
      background: '#ffffff',
      maxWidth: 1820,
      marginLeft: 'auto',
      marginRight: 'auto',
      padding: [20, 0],
      width: 'auto',
      display: 'flex',
      alignItems: 'baseline'
    },
    loader: {
      position: 'absolute',
      left: '50%',
      top: 0,
      borderWidth: '5px',
      marginLeft: '-18px',
      paddingTop: 38,
      '&:before': {
        border: {
          width: 5
        }
      }
    },
    modal: {
      zIndex: 2050
    },
    input: {
      fontWeight: 400,
      fontSize: 14,
      border: {
        color: '#c8c8c8',
        radius: 3,
        style: 'solid',
        width: 1,
      },
      boxSizing: 'border-box',
      color: '#333',
      fontFamily: 'Arial, sans-serif',
      height: 40,
      padding: '6px 10px',
      width: '100%',

      '&[disabled]': {
        opacity: 0.5
      }
    },
    colors: {
      white: '#ffffff',
      backgroundLight: '#f2f2f2',
      backgroundSubtle: '#ededed',
      borderDark: '#414141',
      borderMid: '#757575',
      borderLight: '#d2d2d2',
      borderSubtle: '#f5f5f5',
      brand: '#0070b9',
      brandDarker: '#336699',
      shadowDark: 'rgba(0, 0, 0, 0.2)',
      shadowLight: '#ccc',
      text: '#333',
      textDark: '#414141',
      textDisabled: '#AAA',
      textMid: '#6e6e6e',
      textSubtle: '#888'
    },
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      padding: 0,
      margin: -1,
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      border: 0
    },
    modalFooter: {
      border: 0,
      textAlign: 'left',
      padding: [10, 0, 20, 0],
      display: 'flex'
    },
    modalBody: {
      position: 'relative',
      padding: [20, 0],
      display: 'flex'
    },
    ptxAlert: {
      margin: [0, '4%'],
      position: 'fixed',
      backgroundColor: '#ffffff',
      left: 0,
      right: 0,
      top: 126,
      zIndex: 30
    },
    link: {
      color: '#0070b9',
      colorHover: '#0070b9' // Todo: Get this value from designer.
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      height: 34,
      fontSize: 14,
      margin: [0, 12],
      padding: [0, 12],
      borderWidth: 1,
      '&:first-child': {
        marginLeft: 0
      },
      '&:last-child': {
        marginRight: 0
      },
      border: {
        width: 1
      }
    },
    label: {
      color: '#6e6e6e',
      cursor: 'default',
      display: 'block',
      fontSize: 12,
      fontWeight: 700,
      lineHeight: '14px',
      marginBottom: 6,
      textTransform: 'uppercase'
    },
    noHorizontalSpaces: {
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: 0,
      paddingRight: 0
    },
    formInput: {
      fontWeight: 400,
      maxWidth: 340
    },
    layout: {
      separation: 0
    },
    formComponents: {
      select: {
        caret: {
          display: 'none'
        }
      }
    },
    noPrint: {
      '@media print': {
        display: 'none !important'
      }
    }
  },
};

themes.ash = (themeFromCore) => ({
  ...themes.legacy,
  theme: 'ash',
  responsiveGrid,
  breakpoints,
  layoutManager: {
    fontFamily: '"Roboto", Helvetica, Arial, sans-serif'
  },
  mainContent: {
    paddingTop: [0, '!important']
  },
  gluContainer: {
    ...themes.legacy.gluContainer,
    padding: [20, themeFromCore.spacing.pageMarginValues.mobile],
    [`@media screen and (max-width: ${themeFromCore.spacing.pageMarginBreakpoints.tablet - 1}px)`]: {
      marginLeft: 15,
      marginRight: 15
    }
  },
  pageHeadline: {
    position: 'fixed',
    zIndex: 1000,
    top: 0,
    width: '100%',
    maxWidth: 1820,
    background: '#4c4c4c',
    height: 50,
    margin: 0
  },
  documentHeader: {
    padding: [0, themeFromCore.spacing.pageMarginValues.mobile],
    margin: [0, -themeFromCore.spacing.pageMarginValues.mobile],
    [`@media screen and (min-width: ${themeFromCore.spacing.pageMarginBreakpoints.tablet}px) and (max-width: ${themeFromCore.spacing.pageMarginBreakpoints.desktop - 1}px)`]: {
      padding: [0, themeFromCore.spacing.pageMarginValues.tablet],
      margin: [0, -themeFromCore.spacing.pageMarginValues.tablet],
      flexWrap: 'nowrap'
    },
    [`@media screen and (min-width: ${themeFromCore.spacing.pageMarginBreakpoints.desktop}px) and (max-width: ${themeFromCore.spacing.pageMarginBreakpoints.desktopLarge}px)`]: {
      padding: [0, themeFromCore.spacing.pageMarginValues.desktop],
      margin: [0, -themeFromCore.spacing.pageMarginValues.desktop],
      flexWrap: 'nowrap'
    },
    [`@media screen and (min-width: ${themeFromCore.spacing.pageMarginBreakpoints.desktopLarge + 1}px)`]: {
      padding: [0, [`max(calc((100vw - ${themeFromCore.spacing.pageMargins['@media screen and (min-width: 1852px)'].maxWidth}px) / 2), ${themeFromCore.spacing.pageMarginValues.desktop}px)`]],
      margin: [0, [`min(calc((100vw - ${themeFromCore.spacing.pageMargins['@media screen and (min-width: 1852px)'].maxWidth}px) / -2), -${themeFromCore.spacing.pageMarginValues.desktop}px)`]],
      flexWrap: 'nowrap'
    }
  },
  breakpointQueries: Object.keys(breakpoints).reduce((acc, bp) => ({
    ...acc,
    [bp]: `@media screen and (min-width: ${breakpoints[bp]}px)`
  }), {}),
  brand: {
    background: '#1fa2ff',
    backgroundLight: '#ade2ff',
    backgroundLightOpaque: 'rgba(173, 226, 255, 0.3)',
    gradient: 'linear-gradient(to bottom, #02dfeb 0%, #0971B8 100%)',
    gradientDark: 'linear-gradient(to bottom, #308188 0%, #016fb6 100%)'
  },
  colors: {
    white: '#ffffff',
    backgroundLight: '#f2f2f2',
    backgroundSubtle: '#ededed',
    borderDark: '#414141',
    borderMid: '#757575',
    borderLight: '#d2d2d2',
    borderSubtle: '#f5f5f5',
    brand: '#0070b9',
    brandDarker: '#336699',
    shadowDark: 'rgba(0, 0, 0, 0.2)',
    shadowLight: '#ccc',
    text: '#333',
    textDark: '#414141',
    textDisabled: '#AAA',
    textMid: '#6e6e6e',
    textSubtle: '#888'
  },
  page: {
    margin: '0 auto',
    maxWidth: responsiveGrid.desktopLarge,
    padding: {
      bottom: 15,
      top: 15
    },
    position: 'relative'
  },
  link: {
    color: '#0070b9',
    colorHover: '#0070b9' // Todo: Get this value from designer.
  },
  navigation: {
    width: 68
  },
  subNavigation: {
    width: 350
  },
  grid: {
    borderColor: '#ededed',
    header: {
      color: '#3e3e3e'
    }
  },
  fonts: {
    robotoCondensed: {
      fontFamily: '"Roboto Condensed", "Helvetica Narrow", "Arial Narrow", Arial, sans-serif',
      fontStretch: 'condensed',
      fontWeight: 400
    },
    titleCondensed: '"Barlow Condensed", "Helvetica Narrow", "Arial Narrow", Arial, sans-serif'
  },
  pageTitle: {
    fontFamily: '"Barlow Condensed", "Helvetica Narrow", "Arial Narrow", Arial, sans-serif',
    fontSize: 50,
    fontStretch: 'condensed',
    fontWeight: 300,
    height: 104,
    lineHeight: 'unset'
  },
  headline: {
    fontSize: 30,
    fontWeight: 400,
    lineHeight: '35px',
    marginTop: 0
  },
  subHeading: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '19px',
    textTransform: 'uppercase'
  },
  label: {
    color: '#6e6e6e',
    cursor: 'default',
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '14px',
    marginBottom: 6,
    textTransform: 'uppercase'
  },
  input: {
    border: {
      color: '#c8c8c8',
      radius: 3,
      style: 'solid',
      width: 1,
    },
    boxSizing: 'border-box',
    color: '#333',
    fontFamily: 'Arial, sans-serif',
    height: 40,
    padding: '6px 10px',
    width: '100%',

    '&[disabled]': {
      opacity: 0.5
    }
  },
  error: {
    color: '#df0000',
    fontSize: 12,
    marginBottom: 0,
    marginTop: 6
  },
  statuses: {
    approved: '#1FA2FF',
    complete: '#32c846',
    default: '#00c0f0',
    sending: '#EDEDED',
    rejected: '#ED5481'
  },
  card: {
    radius: 4
  },
  // styling fixes for @glu/modal-react
  modalReact: {
    close: {
      right: '12px '
    },
    header: {
      position: 'absolute',
      width: '100%'
    },
    icon: {
      marginBottom: '15px',
      '& > svg': {
        width: '40px',
        height: '40px',
      }
    },
    borderLeft: {
      left: '-5px',
      top: '-1px',
    },
  },
  modalBody: {
    ...themes.legacy.modalBody
  },
  modalFooter: {
    ...themes.legacy.modalFooter,
    display: 'flex',
    justifyContent: 'center',
    margin: [0, 'auto'],
    '& > button': {
      margin: [0, 12]
    }
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    height: 40,
    fontSize: 16,
    margin: [0, 12],
    padding: [0, 24],
    borderWidth: 1,
    '&:first-child': {
      marginLeft: 0
    },
    '&:last-child': {
      marginRight: 0
    }
  },
  customPageHeadline: {},
  pageTitleContainer: {
    padding: [0, 24],
    width: 'auto',
    minWidth: 'auto',
    borderBottom: {
      width: 1,
      style: 'solid',
      color: '#EDEDED'
    }
  },
  pageBack: {
    marginRight: 10,

    '& .back-icon': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        background: '#f4f7fa',
        textDecoration: 'none'
      },
      color: '#0070b9 !important',
      width: 40,
      height: 40,
      borderRadius: 40,
      '& > svg': {
        fill: '#0070b9 !important',
        width: 23,
        height: 22,
        fontSize: 20
      }
    }
  },
  tabs: {
    indicator: {
      backgroundColor: '#1fa2ff'
    },
    tab: {
      color: '#0074bd'
    }
  },
  popover: {
    content: {
      backgroundColor: {
        defaultBackgroundColor: '#0070b9'
      }
    },
    title: {
      color: '#ffffff',
      fontSize: 16,
      lineHeight: '22px'
    }
  },
  root: {
    '& .printers-container, .document-profiles-container, #region-btsProfiles-grid': {
      marginTop: 37
    },

    '& .glu-container': {
      padding: 0,
      minWidth: 'unset',
      maxWidth: 'none',

      '& #region-tabs-scroll': {
        marginTop: 0
      },

      '& .page-actions .btn-tertiary': {
        height: 12
      },

      '& .batch-upload, .cheque-batch-summary-details, #region-contacts-grid, #contactForm, #btsProfileDetailsForm, #region-dom-projects-grid, #projectForm, #region-linked-profiles-list, #region-grid, #region-cheque-book, #region-linked-document-profiles, #region-roles-grid, #roleForm, #region-app-form, #region-logos-grid': {
        marginTop: 32
      }
    },

    '& [class*=col-12], [class*=row]': {
      padding: 0
    },

    '& .btn-group > .btn': {
      margin: 0
    },

    '& .btn': {
      height: 40,
      fontSize: 16,
      padding: [0, 24],
      margin: [0, 12],
      display: 'flex',
      justifyContent: 'center',
      minWidth: 'unset',
      alignItems: 'center',
      '&:first-child': {
        marginLeft: 0
      },
      '&:last-child': {
        marginRight: 0
      },
      '&[disabled]': {
        opacity: 0.5,
        cursor: 'not-allowed'
      }
    },

    '& .file-upload': {
      '& .error': {
        '& .status-icon,.message': {
          color: '#ffffff !important'
        }
      }
    },

    '& .nav-tabs': {
      display: 'flex',

      '& li': {
        margin: [0, 22],

        '&:first-child': {
          marginLeft: 0
        },

        '&:last-child': {
          marginRight: 0
        }
      },

      '& li a': {
        color: '#0074bd',
        fontFamily: '"Roboto Condensed", "Helvetica Narrow", "Arial Narrow", Arial, sans-serif',
        fontSize: 20,
        padding: [10, 0],
        paddingTop: '34px !important'
      },

      '& li.disabled a': {
        opacity: 0.5
      },

      '& li.active a': {
        color: '#000000',
        borderBottom: {
          width: 3,
          style: 'solid',
          color: '#1fa2ff'
        }
      },

      '& .integration-outbox-watch': {
        marginLeft: 'auto'
      },

      '& .profile-mode-switch-container': {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
        '& .profile-mode-switch': {
          margin: 0
        },
        '& .actions-dropdown': {
          margin: [0, 0, 0, 15]
        }
      }
    },

    // styles for alert.info for newDataAvaliable
    '& div > .alert-info': {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'center',
      '& .close': {
        margin: [25, 16]
      },
      '& p': {
        display: 'flex',
        alignItems: 'center',
        fontFamily: '"Roboto Condensed", Roboto, Helvetica, Arial, sans-serif',
        fontSize: 16,
        marginBottom: 0,
        '& button': {
          padding: 0
        },
        '& strong': {
          whiteSpace: 'pre'
        },
        padding: [10, 0, 10, 10]
      },
      '& p:last-child': {
        padding: [10, 0, 10, 5]
      },
      '& p + p': {
        margin: 0
      },
      '& ol': {
        paddingLeft: 10,
        '& li': {
          listStyle: 'decimal',
          marginLeft: 40
        }
      },
      '&.multi-line-alert': {
        flexDirection: 'column',
        '& > *': {
          marginRight: 'auto'
        }
      }
    },

    '& label.optional': {
      marginBottom: 9
    },
    '& .grid': {
      '& .action-cell': {
        fontSize: 12,
        '& > .btn': {
          fontSize: 12,
          justifyContent: 'left',
          height: 30,
          padding: [0, 12],
          float: 'left'
        }
      },

      '& .header-checkbox.grid-row-selector': {
        width: '100%',

        '& .pseudoCheckbox': {
          marginTop: 0
        },

        '& label': {
          width: 'auto',
          fontSize: 12
        }
      },

      '& .grid-filter-selection': {
        display: 'flex !important',
        alignItems: 'flex-end',

        '& #region-field-equality': {
          marginTop: 3
        },

        '& .search-operand': {
          marginBottom: -5
        },

        '& .dynamic-search-control': {
          '& .form-inline': {
            display: 'flex',
            alignItems: 'center',

            '& .glu-range-date-picker': {
              marginBottom: -5
            }
          },
        }
      },
      '& .grid-manage-columns-buttons': {
        display: 'flex'
      },
      '& .bulk-actions': {
        display: 'flex'
      },
      '& .grid-row-buttons': {
        '& .btn-group': {
          display: 'flex',
          alignItems: 'center'
        },
        '& .dropdown-toggle': {
          marginLeft: 15,
          padding: [5, 13],
          '&:hover': {
            color: '#ffffff',
            borderRadius: [40, '!important']
          }
        }
      },
      '& .cheque-voided-row': {
        '& .grid-row-buttons': {
          padding: [11, 16]
        },
      },
      '& .grid-row': {
        '& .pseudoCheckbox': {
          marginTop: [-12, '!important']
        },
        '& td > .btn': {
          padding: 0,
          '&.btn-link-style': {
            height: 'initial'
          }
        }
      },
      '& .grid-row.grid-row-even': {
        backgroundColor: '#f5f5f5 !important',
        '&.test-mode-row': {
          backgroundColor: '#fff9f2 !important'
        }
      },
      '& .grid-row-group-header': {
        '& td': {
          backgroundColor: '#eaeaea !important',
        }
      },
      '& .footer .pager .paginate-advanced': {
        textTransform: 'uppercase',
        fontSize: 10,
        '& .grid-page-size-controls nav': {
          marginRight: 24
        },
        '& .icon-caret-down': {
          lineHeight: 1.6,
          fontSize: 8
        }
      }
    },

    '& .page-actions': {
      display: 'flex',
      alignItems: 'center',
      padding: [40, 0, 20, 0]
    },
    '& .page-actions.top-padding-14': {
      paddingTop: 14
    },
    '& .page-actions.sticky-bar, .nav-tabs.sticky-bar': {
      marginLeft: '0 !important',
      marginRight: '0 !important',
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
      left: 24,
      right: 24,
      width: 'auto',
      [`@media screen and (min-width: ${themeFromCore.spacing.pageMarginBreakpoints.tablet}px)`]: {
        left: `max(calc((100vw - ${themeFromCore.spacing.pageMarginBreakpoints.desktop}px) / 2), ${themeFromCore.spacing.pageMarginValues.tablet}px)`,
        right: `max(calc((100vw - ${themeFromCore.spacing.pageMarginBreakpoints.desktop}px) / 2), ${themeFromCore.spacing.pageMarginValues.tablet}px)`,
      },
      [`@media screen and (min-width: ${themeFromCore.spacing.pageMarginBreakpoints.desktop}px)`]: {
        left: `max(calc((100vw - ${themeFromCore.spacing.pageMarginValues.desktopLargeMaxWidth}px) / 2), ${themeFromCore.spacing.pageMarginValues.desktop}px)`,
        right: `max(calc((100vw - ${themeFromCore.spacing.pageMarginValues.desktopLargeMaxWidth}px) / 2), ${themeFromCore.spacing.pageMarginValues.desktop}px)`,
        maxWidth: `${themeFromCore.spacing.pageMarginValues.desktopLargeMaxWidth}`
      }
    },

    '& .nav-tabs.sticky-bar': {
      padding: '20px 24px 0 24px !important',
    },
    '& #region-actions': {
      '& .batch-actions': {
        margin: '30px 0 !important'
      },
      '& .col-12': {
        display: 'flex'
      }
    },
    '& .bts-batch-detail': {
      alignItems: 'baseline'
    },

    '& .date-ranges': {
      marginTop: -20,
      display: 'flex',
      flexWrap: 'wrap',
      '& .data-range-item': {
        display: 'flex',
        padding: 0,
        alignItems: 'center',
        '& > .btn': {
          padding: 0,
          margin: '5px !important'
        },
        '& #region-date-picker': {
          marginTop: 20,
          marginLeft: 5
        }
      }
    },

    '& .outbox-activity-container, .documents-at-risk-container': {
      padding: 0
    },
    '& .outbox-activity-container': {
      marginLeft: 0,
      marginRight: 10
    },
    '& .documents-at-risk-container': {
      marginLeft: 10,
      marginRight: 0
    },

    '& .widget': {
      padding: 0,

      '& .widget-body': {
        padding: 20
      }
    },
    '& .widget:before': {
      right: 0,
      left: 0
    },

    '& #region-time-frame-selection': {
      '& .flex-dropdown': {
        width: 'auto',
        marginRight: 5
      }
    },
    '& .outbox': {
      '& .page-actions': {
        justifyContent: 'flex-end'
      },
      '& #clear-selection-btn': {
        marginLeft: 'auto'
      }
    },

    '& #region-related-options > div': {
      display: 'flex'
    },

    '& .quick-filters': {
      alignItems: 'center',
      display: 'flex',

      '& .quick-filter-label': {
        marginBottom: 0
      }
    },

    '& .printers-actions': {
      display: 'flex',
      alignItems: 'center',
      margin: [10, 0, 20],
      '& .printer-queue-filter': {
        margin: 0
      },
      '& .delete-orphaned-printers-button': {
        marginLeft: 'auto'
      }
    },

    '& .print-job-search-form': {
      marginLeft: -10
    },

    '& #region-available-printers': {
      '& .panel-heading': {
        display: 'flex',
        alignItems: 'center',
        '& .actions-container': {
          alignItems: 'center',
          display: 'flex',
          marginLeft: 'auto',

          '& .print-actions': {
            alignItems: 'center',
            display: 'flex',

            '& .btn .caret': {
              marginLeft: 5
            },

            '& .action-btn': {
              left: 'auto',
              top: 'auto',
              margin: [0, 0, 0, 'auto'],
              position: 'relative'
            }
          }
        }
      }
    }
  },
  '@font-face': fontFace
});

export const { legacy, ash } = themes;

export default themes;
